import { createReducer } from '__common/utils/helpers';
import { LiteHistoryActionTypes } from './liteHistoryActionsConstants';

// Lite-only history slice. Same shape as the legacy `history` slice, but
// PUT correctly drops the redo branch and resets `index` so that a new
// edit after one or more undos doesn't leak the discarded "future"
// snapshots back into the stack. Legacy `history` is left untouched.
//
// Notably we DON'T listen for legacy CLEAR_HISTORY/RESET_HISTORY here,
// because panelsEditor.tsx fires those during normal init / background
// redraw / zoom — events that should not wipe Lite's undo stack. Instead
// the workspace container dispatches LITE_SEED_HISTORY explicitly once
// panels finish loading, which establishes the "anchor" snapshot and
// makes undo bottom out at the loaded state rather than at empty.

export type liteHistoryState = {
  panelsSnapshot: any[];
  index: number;
  maxHistory: number;
  canUndo: boolean;
  canRedo: boolean;
};

const initialState: liteHistoryState = {
  panelsSnapshot: [[]],
  index: 0,
  maxHistory: 50,
  canUndo: false,
  canRedo: false,
};

export default {
  liteHistory: createReducer(initialState, {
    [LiteHistoryActionTypes.PUT_LITE_PANELS_IN_HISTORY](state: liteHistoryState, action) {
      // Snapshots are stored newest-first (unshift), and `index` tracks
      // how far back we've undone. Snapshots[0..index-1] are the "redo
      // branch" — we got there by undoing, but a brand-new change has
      // just been made, so that branch is no longer reachable and must
      // be dropped. The legacy reducer skips this step and the user's
      // next undo walks into stale snapshots.
      const trimmed = state.panelsSnapshot.slice(state.index);
      // Cap at maxHistory entries (after we've added the new one).
      while (trimmed.length >= state.maxHistory) {
        trimmed.pop();
      }
      trimmed.unshift(action.payload.panels);
      return {
        ...state,
        panelsSnapshot: trimmed,
        index: 0,
        canUndo: trimmed.length > 1,
        canRedo: false,
      };
    },
    [LiteHistoryActionTypes.LITE_UNDO_HISTORY](state: liteHistoryState) {
      const newIndex = (state.index < state.maxHistory && state.index + 1 < state.panelsSnapshot.length)
        ? state.index + 1
        : state.index;
      const atOldest = newIndex === state.panelsSnapshot.length - 1;
      return {
        ...state,
        index: newIndex,
        canUndo: !atOldest,
        canRedo: newIndex > 0,
      };
    },
    [LiteHistoryActionTypes.LITE_REDO_HISTORY](state: liteHistoryState) {
      const newIndex = state.index > 0 ? state.index - 1 : state.index;
      return {
        ...state,
        index: newIndex,
        canRedo: newIndex > 0,
        canUndo: newIndex < state.panelsSnapshot.length - 1,
      };
    },
    [LiteHistoryActionTypes.LITE_SEED_HISTORY](state: liteHistoryState, action) {
      // Hard reset to a single anchor snapshot of the just-loaded panels.
      // canUndo=false because the loaded state is the "zero point" — the
      // user can't undo past server-committed history into nothing.
      const panels = action.payload && action.payload.panels ? action.payload.panels : [];
      return {
        ...state,
        panelsSnapshot: [panels],
        index: 0,
        canUndo: false,
        canRedo: false,
      };
    },
    [LiteHistoryActionTypes.LITE_RESTORE_HISTORY](state: liteHistoryState, action) {
      // Restore a full history state saved from a previous roof visit.
      const { panelsSnapshot, index, canUndo, canRedo } = action.payload;
      return {
        ...state,
        panelsSnapshot: panelsSnapshot || [[]],
        index: index || 0,
        canUndo: !!canUndo,
        canRedo: !!canRedo,
      };
    },
  }),
};
